import { useEffect, type ReactElement } from "react";
import { usePublicSiteConfigContext } from "@Front/Services/PublicSiteConfig/usePublicUiOptions";
import { getNotaryPublicDarkShellThemeCssVars } from "@Front/themes/notaryPublicDarkShellThemeCssVars";

/**
 * Applies `theme.css_vars` from `PublicSiteConfigProvider` on `document.documentElement`.
 * When `public_ui.merge_dark_public_shell_theme` is true, merges the dark public shell CSS vars.
 */

function applyCssVars(css: Record<string, string>): void {
	for (const [k, v] of Object.entries(css)) {
		if (typeof v === "string" && v.length > 0) {
			document.documentElement.style.setProperty(k, v);
		}
	}
}

export function useApplyPublicSiteTheme(): void {
	const { config } = usePublicSiteConfigContext();

	useEffect(() => {
		if (!config) {
			return;
		}
		applyCssVars(config.theme?.css_vars ?? {});
		if (config.public_ui.merge_dark_public_shell_theme) {
			applyCssVars(getNotaryPublicDarkShellThemeCssVars());
		}
		if (config.product_code) {
			document.documentElement.setAttribute("data-lecoffre-product", config.product_code);
		}
	}, [config]);
}

/** Mount as child of `PublicSiteConfigProvider` (hooks in `_app` run outside that provider). */
export function PublicSiteThemeEffect(): ReactElement | null {
	useApplyPublicSiteTheme();
	return null;
}
